import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { safeTrackException } from '../appInsights';
import { type AuthAccount, getAuthClient } from './auth/authClient';
import { Utils } from './Utils';

/**
 * Owns every read of the token cache (MSAL for B2C, or Auth0 - see `auth/authClient`). One access token is shared by every request the app
 * makes, so when it stops being accepted, every open screen fails at the same instant - the guards
 * below exist to keep that one failure from becoming one B2C call, or one red toast, per request.
 */

// A settled forced refresh is held for this long so that a burst of 401s arriving after it
// completes reuses the result instead of asking B2C again for a token the server just rejected.
const FORCED_REFRESH_COOLDOWN_MS = 30_000;

// acquireTokenRedirect normally never settles - the page navigates away first. If it does settle,
// the navigation was refused, so the guard is released after a grace period long enough to have
// swallowed the burst that raised it.
const REDIRECT_GUARD_RELEASE_MS = 5_000;

let pendingForcedRefresh: Promise<void> | null = null;
let redirectingToLogin = false;

export function getActiveAccount(): AuthAccount | null {
  return getAuthClient().getAccount();
}

/**
 * Whether MSAL is saying the user must sign in again, as opposed to a failure that says nothing
 * about the session. Deliberately narrower than the check in `Data.getAccessToken`, which also
 * treats any BrowserAuthError as fatal: this one gates a redirect that would throw away a session
 * that may well still be good.
 */
export function requiresInteraction(error: unknown): boolean {
  return getAuthClient().isInteractionRequired(error);
}

/** The cached token. The client only goes to the identity provider near expiry. */
export function acquireAccessToken(): Promise<string> {
  return getAuthClient().getAccessToken();
}

/**
 * Throws away the cached token so the next acquire fetches a new one from B2C. Used when the
 * server rejected a token MSAL still considered valid - clock skew between the browser and the
 * server does this, and so does a signing-key rollover mid-session.
 *
 * Callers share one round trip, and keep sharing it for a cooldown after it settles. Without that,
 * a screen with ten live queries would answer one rejected token with ten refreshes and risk being
 * throttled by B2C exactly when the user most needs to get back in.
 */
export function forceTokenRefresh(): Promise<void> {
  pendingForcedRefresh ??= getAuthClient()
    .getAccessToken({ forceRefresh: true })
    .then(() => {
      setTimeout(() => {
        pendingForcedRefresh = null;
      }, FORCED_REFRESH_COOLDOWN_MS);
    })
    .catch((error) => {
      // Nothing was cached, so let the next caller try rather than serving them this failure.
      pendingForcedRefresh = null;
      throw error;
    });
  return pendingForcedRefresh;
}

/**
 * Sends the user back to B2C to sign in again. This is a full page navigation, so it must happen
 * once however many requests discovered the dead session together - a second acquireTokenRedirect
 * raised while the first is still resolving throws `interaction_in_progress`.
 */
export function redirectToLogin(): void {
  if (redirectingToLogin) return;

  redirectingToLogin = true;
  Utils.clearLoginSessionUiFlags();
  // The requests that hit this are failed locally and reported to nobody, so without this line a
  // total MSAL or B2C outage would be invisible in telemetry. Raised here rather than per request
  // because this guard is what makes it one event per dead session.
  safeTrackException(
    new Error('Session lost, re-authenticating'),
    { source: 'auth' },
    SeverityLevel.Information
  );

  Promise.resolve(getAuthClient().loginRedirect())
    .catch(() => undefined)
    .finally(() => {
      // Release rather than strand the session on a page where every request fails and nothing is
      // left that could re-authenticate it.
      setTimeout(() => {
        redirectingToLogin = false;
      }, REDIRECT_GUARD_RELEASE_MS);
    });
}

// The back button restores this page from the bfcache with module state intact, so a session that
// redirected to B2C and then came Back would return with the guard still raised and nothing left
// that could ever re-authenticate it. (`previewIdentity` guards its own module state the same way.)
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) redirectingToLogin = false;
  });
}

/** Test seam: the guards above are module state and would otherwise leak between test cases. */
export function resetAuthTokenState(): void {
  pendingForcedRefresh = null;
  redirectingToLogin = false;
}
